// "Manage X needs View X" for the Role Catalog checklist.
//
// The rule itself lives in the backend catalog (PERMISSION_PREREQUISITES) and
// arrives on each permission as `requires`, so there is no second copy here to
// drift. The server applies the same closure on save; doing it in the
// checklist too means the boxes an admin sees are exactly what gets stored.

import { PermissionCategory } from "../../../../services/roles.service";

export type RequiresMap = Map<string, string[]>;

export function buildRequiresMap(catalog: PermissionCategory[]): RequiresMap {
  const map: RequiresMap = new Map();
  for (const category of catalog) {
    for (const p of category.permissions) {
      if (p.requires && p.requires.length > 0) map.set(p.code, p.requires);
    }
  }
  return map;
}

/** Grant `code` and everything it transitively requires. */
export function grantWithPrerequisites(codes: Set<string>, code: string, requires: RequiresMap): Set<string> {
  const next = new Set(codes);
  const pending = [code];
  while (pending.length > 0) {
    const c = pending.pop()!;
    if (next.has(c) && c !== code) continue;
    next.add(c);
    for (const needed of requires.get(c) ?? []) {
      if (!next.has(needed)) pending.push(needed);
    }
  }
  return next;
}

/** Revoke `code` and everything that transitively depends on it. */
export function revokeWithDependents(codes: Set<string>, code: string, requires: RequiresMap): Set<string> {
  const next = new Set(codes);
  const pending = [code];
  while (pending.length > 0) {
    const c = pending.pop()!;
    if (!next.delete(c) && c !== code) continue;
    for (const [dependent, needs] of requires) {
      if (needs.includes(c) && next.has(dependent)) pending.push(dependent);
    }
  }
  return next;
}
